import { useCallback, useEffect, useState } from 'react';
import type { CandidateRow } from '@shared/models/element-import.model';

export const STATUS_FILTER_ROW_COLLAPSE_MS = 250;

const findExitInsertIndex = (
  prev: readonly AnimatedStatusFilterRow[],
  next: readonly AnimatedStatusFilterRow[],
  exitingRowId: string,
): number => {
  const prevIndex = prev.findIndex((entry) => entry.row.id === exitingRowId);
  if (prevIndex < 0) return next.length;

  for (let i = prevIndex - 1; i >= 0; i--) {
    const anchor = prev[i];
    if (!anchor) continue;
    const anchorIndex = next.findIndex((n) => n.row.id === anchor.row.id);
    if (anchorIndex >= 0) return anchorIndex + 1;
  }
  for (let i = prevIndex + 1; i < prev.length; i++) {
    const anchor = prev[i];
    if (!anchor) continue;
    const anchorIndex = next.findIndex((n) => n.row.id === anchor.row.id);
    if (anchorIndex >= 0) return anchorIndex;
  }
  return next.length;
};

export type AnimatedStatusFilterRow = {
  row: CandidateRow;
  /** When false, the row plays a collapse-out animation before unmount. */
  visible: boolean;
};

/** Pure transition step — exported for unit tests. */
export const buildDisplayRows = (
  prev: readonly AnimatedStatusFilterRow[],
  rowsWithStatus: readonly { row: CandidateRow }[],
  targetVisibleRowIds: ReadonlySet<string>,
): AnimatedStatusFilterRow[] => {
  const rowsById = new Map(rowsWithStatus.map(({ row }) => [row.id, row]));
  const targetFor = (rowId: string): boolean =>
    rowsById.has(rowId) && targetVisibleRowIds.has(rowId);

  const exitingIds = new Set(
    prev
      .filter((entry) => !targetFor(entry.row.id))
      .map((entry) => entry.row.id),
  );

  const next: AnimatedStatusFilterRow[] = [];

  for (const { row } of rowsWithStatus) {
    if (!targetFor(row.id)) continue;
    next.push({ row, visible: true });
  }

  for (const entry of prev) {
    if (!exitingIds.has(entry.row.id)) continue;
    if (next.some((n) => n.row.id === entry.row.id)) continue;
    const row = rowsById.get(entry.row.id) ?? entry.row;
    const insertAt = findExitInsertIndex(prev, next, entry.row.id);
    next.splice(insertAt, 0, { row, visible: false });
  }

  return next;
};

/**
 * Keeps rows in the list while they collapse out whenever they leave the
 * filtered view — status-summary segment, search, or a row status change.
 * Visible rows follow {@link rowsWithStatus} order; exiting rows stay in place
 * until the collapse animation finishes.
 */
export const useAnimatedStatusFilterRows = (
  rowsWithStatus: readonly { row: CandidateRow }[],
  targetVisibleRowIds: ReadonlySet<string>,
): {
  displayRows: AnimatedStatusFilterRow[];
  onRowExitComplete: (rowId: string) => void;
} => {
  const [displayRows, setDisplayRows] = useState<AnimatedStatusFilterRow[]>(
    () => buildDisplayRows([], rowsWithStatus, targetVisibleRowIds),
  );

  useEffect(() => {
    setDisplayRows((prev) =>
      buildDisplayRows(prev, rowsWithStatus, targetVisibleRowIds),
    );
  }, [rowsWithStatus, targetVisibleRowIds]);

  const onRowExitComplete = useCallback((rowId: string) => {
    setDisplayRows((prev) => prev.filter((entry) => entry.row.id !== rowId));
  }, []);

  return { displayRows, onRowExitComplete };
};
